import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { submitImageSearch } from '@/redux/modules/search/imageSearch/imageSearch.actions';
import { resetImageSearch } from '@/redux/modules/search/imageSearch/imageSearch.reducer';
import {
    getImageSearchError,
    getImageSearchResults,
    getImageSearchStatus,
    getImageSearchThumbnailUrl,
} from '@/redux/modules/search/imageSearch/imageSearch.selectors';

/**
 * Single entry point for the image-search flow. The upload surfaces call `submit(file)` and
 * navigate to the results page on success; the results page reads `results` / `thumbnailUrl`.
 * `submit` resolves the dispatched thunk so callers can branch on `submitImageSearch.fulfilled.match`.
 */
export const useImageSearch = () => {
    const dispatch = useAppDispatch();
    const status = useAppSelector(getImageSearchStatus);
    const error = useAppSelector(getImageSearchError);
    const results = useAppSelector(getImageSearchResults);
    const thumbnailUrl = useAppSelector(getImageSearchThumbnailUrl);

    const submit = useCallback(
        (file: File) => {
            // Free the prior search's thumbnail before a new one replaces it — the reducer stays
            // pure, so object-URL revocation lives here at the component layer.
            if (thumbnailUrl) {
                URL.revokeObjectURL(thumbnailUrl);
            }
            return dispatch(submitImageSearch(file));
        },
        [dispatch, thumbnailUrl]
    );

    const reset = useCallback(() => {
        if (thumbnailUrl) {
            URL.revokeObjectURL(thumbnailUrl);
        }
        dispatch(resetImageSearch());
    }, [dispatch, thumbnailUrl]);

    return { error, reset, results, status, submit, thumbnailUrl };
};
